import { pubText, pubFormat } from "../i18n/publicTranslation";
import React, { useEffect, useState } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { DEFAULT_MODEL_PRICING, loadModelPricing, modelWordPrice, MODEL_PRICE_WORDS, ModelPricingCatalog, ModelPriceSort, sortPricingModels } from "../api/modelPricing";
import { APP_BORDER, APP_TEXT_SECONDARY } from "./createShared";
import FableTokenAmount from "./common/FableTokenAmount";
import ModelSortMenu from "./common/ModelSortMenu";

const columns = [{ key: "prompt", label: "Input" }, { key: "completion", label: "Output" }] as const;
const number = (value: number) => value.toLocaleString(undefined, {
  maximumFractionDigits: value > 0 && value < 0.001 ? Math.min(20, 2 - Math.floor(Math.log10(value))) : 3,
});

export default function ModelPricingSection({ sort: suppliedSort }: { sort?: ModelPriceSort } = {}) {
  const [catalog, setCatalog] = useState<ModelPricingCatalog>(DEFAULT_MODEL_PRICING);
  const [localSort, setSort] = useState<ModelPriceSort>("overall");
  const sort = suppliedSort ?? localSort;
  useEffect(() => {
    const controller = new AbortController();
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      loadModelPricing(controller.signal).then(value => { if (!controller.signal.aborted) setCatalog(value); }).catch(() => { /* Keep the last available prices. */ });
    };
    refresh();
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => { controller.abort(); window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, []);
  const conversion = typeof catalog.tokensPerUsd === "number" && Number.isFinite(catalog.tokensPerUsd) && catalog.tokensPerUsd > 0 ? catalog.tokensPerUsd : null;
  return (
    <>
      <Typography>{pubText(
        "You pay for both input and output. Input includes your messages and story context; output includes the AI’s replies and drafts. Higher effort lets the AI think more deeply, which can improve response quality but may use more tokens."
      )}</Typography>
      {pubText(
        suppliedSort === undefined && <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ModelSortMenu compact value={sort} onChange={setSort} />
        </Box>
      )}
      {pubText(
        !conversion && <Typography role="status">{pubText("Fable token prices aren’t available yet.")}</Typography>
      )}
      <TableContainer>
        <Table aria-label={pubFormat("Estimated price per __VALUE_0__ words", [MODEL_PRICE_WORDS])} sx={{ tableLayout: "fixed", "& th, & td": { borderColor: APP_BORDER, px: { xs: 0.5, sm: 1 }, py: 1.5 }, "& tr > :first-child": { paddingInlineStart: 0 }, "& tr > :last-child": { paddingInlineEnd: 0 } }}>
          <TableHead sx={{ "& th": { borderTop: `1px solid ${APP_BORDER}` } }}>
            <TableRow>
              <TableCell scope="col" sx={{ width: { xs: "30%", sm: "50%" }, color: "white", fontWeight: 700 }}>{pubText("Model")}</TableCell>
              {pubText(
                columns.map(({ key, label }) => <TableCell key={key} scope="col" align="right" sx={{ color: "white", fontSize: { xs: 13, sm: 15 }, fontWeight: 700 }}>
                  {pubText(label)}
                </TableCell>)
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {pubText(
              sortPricingModels(catalog, sort).map(model => <TableRow key={model.id} aria-label={pubFormat("__VALUE_0__ pricing", [model.label])}>
                <TableCell component="th" scope="row" sx={{ overflowWrap: "anywhere" }}>
                  <Typography color="white" fontSize={{ xs: 13, sm: 15 }} fontWeight={600}>{pubText(model.label)}</Typography>
                  <Typography color={APP_TEXT_SECONDARY} fontSize={12}>{pubText(model.provider)}</Typography>
                </TableCell>
                {pubText(columns.map(({ key }) => {
                  const price = modelWordPrice(model.pricing, key, conversion);
                  return (
                    <TableCell key={key} align="right"
                      sx={{ color: price !== null ? "white" : APP_TEXT_SECONDARY, fontSize: { xs: 12, sm: 15 }, fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: price !== null ? "nowrap" : "normal" }}>{pubText(
                      price !== null ? <FableTokenAmount amount={number(price)} kind="price" /> : "Not available"
                    )}</TableCell>
                  );
                }))}
              </TableRow>)
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
